import { useState } from 'react';
import { deviceTokensAPI } from '../../utils/api';
import { describeDevice, rememberBiometricDeclined, storeDeviceToken } from '../../utils/biometricAuth';
import './Biometric.css';

/**
 * Shown once, in place of the login form, straight after a password sign-in on a
 * device that can do biometrics. Declining is remembered per account, so the
 * question is never asked twice.
 *
 * Accepting asks the server for a device token — the sign-in that just happened
 * is what authorises it — and puts that in the secure store. The password the
 * user typed a second ago is not passed to this component and never reaches the
 * keychain.
 */
export default function BiometricOffer({ email, label, onDone }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const accept = async () => {
    setBusy(true);
    setError('');
    let issued = null;
    try {
      const response = await deviceTokensAPI.issue(describeDevice());
      issued = response.device_token;
      await storeDeviceToken(email, issued?.token, issued?.id);
      onDone();
    } catch {
      // A token the phone could not keep is a token nobody will ever present.
      // Hand it back rather than leaving it occupying one of the five slots.
      if (issued?.id) {
        try { await deviceTokensAPI.revoke(issued.id); } catch { /* it will expire */ }
      }
      setError(`${label} could not be set up on this device. You can turn it on later from your profile.`);
      setBusy(false);
    }
  };

  const decline = () => {
    rememberBiometricDeclined(email);
    onDone();
  };

  return (
    <section className="biometric-offer" aria-labelledby="biometric-offer-title" aria-busy={busy}>
      {/* An h1: the offer REPLACES the form and its "Sign In" heading, so this is
          the only heading the screen has. */}
      <h1 className="biometric-offer__title" id="biometric-offer-title">
        Use {label} to sign in next time?
      </h1>
      <p className="biometric-offer__body">
        This device gets its own sign-in key, kept in its secure store and used only
        to sign you in to NCAD Bookings. Your password is never saved on the device,
        and you can revoke the key at any time from your profile.
      </p>
      {error && <p className="biometric-offer__error" role="alert">{error}</p>}
      <div className="biometric-offer__actions">
        <button type="button" className="biometric-offer__accept" onClick={accept} disabled={busy} autoFocus>
          {busy ? 'Setting up…' : `Use ${label}`}
        </button>
        <button type="button" className="biometric-offer__decline" onClick={decline} disabled={busy}>
          Not now
        </button>
      </div>
    </section>
  );
}
